import type { CustomerPriority, CustomerPriorityList } from '@kalos/kalos-rpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { CustomerPriorityClientService } from '../../tools/helpers';
import { queryKeys } from './constants';

export const customerPrioritiesQueryKey = (userId: number) => [
  queryKeys.customerPriorities.root,
  userId,
];

export const useCustomerPrioritiesQuery = <TSelectData = CustomerPriorityList,>({
  userId,
  enabled = true,
  select,
}: {
  userId: number;
  enabled?: boolean;
  select?: (data?: CustomerPriorityList) => TSelectData;
}) => {
  return useQuery({
    queryFn: async () => {
      const data = await CustomerPriorityClientService.listForCustomer(userId);
      data.results.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
      return data;
    },
    queryKey: customerPrioritiesQueryKey(userId),
    enabled: enabled && !!userId,
    select,
  });
};

const invalidateCustomerPriorities = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: [queryKeys.customerPriorities.root] });

export const useCustomerPriorityCreateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (req: CustomerPriority) => {
      return await CustomerPriorityClientService.Create(req);
    },
    onSuccess: async () => {
      await invalidateCustomerPriorities(queryClient);
    },
  });
};

export const useCustomerPriorityUpdateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (req: CustomerPriority) => {
      return await CustomerPriorityClientService.Update(req);
    },
    onSuccess: async () => {
      await invalidateCustomerPriorities(queryClient);
    },
  });
};

export const useCustomerPriorityDeleteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (req: CustomerPriority) => {
      return await CustomerPriorityClientService.Delete(req);
    },
    onSuccess: async () => {
      await invalidateCustomerPriorities(queryClient);
    },
  });
};
